'use client'

import { useEffect, useRef, useState } from 'react'
import { dispatchRunSafeVideoCoordination } from './run-safe-in-the-dark-video-coordination'

const YOUTUBE_VIDEO_ID = 'o02j6apmloI'
const YOUTUBE_EMBED_BASE_SRC = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&controls=1&modestbranding=1&playsinline=1&rel=0&enablejsapi=1`

// state 0 = ended (YouTube IFrame API player state)
const YOUTUBE_STATE_ENDED = 0

export const RunSafeInTheDarkFounderSaidVideo = ({
  title,
}: {
  title: string
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const isInViewRef = useRef(false)
  // Safari (ITP) cần đúng tham số `origin` để player khởi tạo, nếu không video sẽ kẹt ở màn hình loading.
  const [embedSrc] = useState(() =>
    typeof window === 'undefined'
      ? null
      : `${YOUTUBE_EMBED_BASE_SRC}&origin=${encodeURIComponent(window.location.origin)}`
  )

  useEffect(() => {
    if (!containerRef.current) return

    // Load iframe sớm hơn (trước khi thực sự vào view) để tránh giật khi cuộn tới.
    const lazyLoadObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          lazyLoadObserver.disconnect()
        }
      },
      { rootMargin: '200px 0px' }
    )

    // Chỉ phát/bật âm thanh khi section thực sự nằm trong viewport; đồng thời pause video hero khi video này active.
    const audioObserver = new IntersectionObserver(
      ([entry]) => {
        isInViewRef.current = entry.isIntersecting

        const postCommand = (
          func: 'unMute' | 'mute' | 'setVolume' | 'playVideo' | 'pauseVideo',
          args: unknown[] = []
        ) => {
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func, args }),
            '*'
          )
        }

        if (entry.isIntersecting) {
          postCommand('playVideo')
          postCommand('unMute')
          postCommand('setVolume', [100])
        } else {
          postCommand('mute')
          postCommand('pauseVideo')
        }

        dispatchRunSafeVideoCoordination({
          source: 'founder-said',
          active: entry.isIntersecting,
        })
      },
      { threshold: 0.5 }
    )

    lazyLoadObserver.observe(containerRef.current)
    audioObserver.observe(containerRef.current)

    return () => {
      lazyLoadObserver.disconnect()
      audioObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return

      let data: { event?: string; info?: { playerState?: number } }
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }

      if (
        data.event === 'infoDelivery' &&
        data.info?.playerState === YOUTUBE_STATE_ENDED
      ) {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'seekTo', args: [0, true] }),
          '*'
        )
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
          '*'
        )
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  return (
    <div ref={containerRef} className='absolute inset-0'>
      {shouldLoad && embedSrc && (
        <iframe
          ref={iframeRef}
          className='absolute left-1/2 top-1/2 h-[130.42%] min-h-full w-full min-w-full -translate-x-1/2 -translate-y-1/2'
          src={embedSrc}
          title={title}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          allowFullScreen
          onLoad={() => {
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({ event: 'listening', id: YOUTUBE_VIDEO_ID }),
              '*'
            )

            if (isInViewRef.current) {
              const contentWindow = iframeRef.current?.contentWindow
              ;['playVideo', 'unMute'].forEach((func) => {
                contentWindow?.postMessage(
                  JSON.stringify({ event: 'command', func, args: [] }),
                  '*'
                )
              })
            }
          }}
        />
      )}
    </div>
  )
}
